import { GraphQLClient, RequestOptions } from "graphql-request";
import { getSdk } from "./graphql";
import { AuthStoreInterface } from "../auth/store/AuthStoreInterface";
import { tokenManager } from "./tokenManager";
import DOMPurify from "dompurify";

type GraphQLClientRequestHeaders = RequestOptions["requestHeaders"];

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

export function getPublicApi(apiUrl = API_URL) {
  const url = `${apiUrl}/graphql`;
  // credentials:'include' es OBLIGATORIO para el flujo 2FA: las mutations de
  // verificación reciben el Set-Cookie del device-trust (twofa_device) y el
  // login la reenvía. Sin esto el navegador descarta la cookie cross-origin.
  const client = new GraphQLClient(url, {
    headers: {},
    credentials: "include",
  });
  return getSdk(client);
}

export async function login(auth: AuthStoreInterface, apiUrl = API_URL) {
  const url = `${apiUrl}/graphql`;
  const client = new GraphQLClient(url, {
    headers: {},
    credentials: "include",
  });
  const api = getSdk(client);
  try {
    const data = await api.Login({
      email: auth.email,
      password: auth.password,
    });

    if (data.login) {
      if (data.login.twoFaRequired) {
        auth.setTwoFaState({
          twoFaRequired: true,
          twoFaAction: data.login.twoFaAction ?? null,
          tempToken: data.login.tempToken ?? null,
        });
      } else {
        auth.setTokens({
          accessToken: data.login.accessToken!,
          refreshToken: data.login.refreshToken!,
          accessExpiresIn: data.login.accessExpiresIn!,
          refreshExpiresIn: data.login.refreshExpiresIn!,
        });
      }
    }
  } catch (error) {
    // Login corre FUERA del wrapper del SDK: detectar mantenimiento aca
    // (usuario parado en la pantalla de login durante la ventana).
    detectMaintenance(error);
    // Clear tokens on login error
    tokenManager.clearTokens();
    throw error;
  }
}

interface CancellableRequest<T> {
  promise: Promise<T>;
  cancel: () => void;
  isCancelled: () => boolean;
}

// --- Modo mantenimiento -----------------------------------------------------
// Traefik (o el server) responde 503 con {maintenance: true, message?, until?}
// cuando el sistema esta en mantenimiento. Cualquier request que reciba esa
// respuesta activa el overlay via el listener (lo registra el RootStore).
// graphql-request esparce el body JSON parseado dentro de error.response, o lo
// deja crudo en error.response.error; se toleran ambas formas.

export interface MaintenanceSignal {
  message?: string | null;
  until?: string | null;
}

let maintenanceListener: ((info: MaintenanceSignal) => void) | null = null;

export function setMaintenanceListener(fn: (info: MaintenanceSignal) => void) {
  maintenanceListener = fn;
}

/** Variante para responses fetch crudas: parsea el body si es 503. */
export async function detectMaintenanceResponse(
  response: Response
): Promise<boolean> {
  if (response.status !== 503) return false;
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  if (!body || body.maintenance !== true) return false;
  maintenanceListener?.({
    message: body.message ?? null,
    until: body.until ?? null,
  });
  return true;
}

export function detectMaintenance(error: any): boolean {
  const resp = error?.response;
  if (!resp || resp.status !== 503) return false;
  const body =
    resp.maintenance !== undefined
      ? resp
      : typeof resp.error === "object" && resp.error !== null
      ? resp.error
      : null;
  if (!body || body.maintenance !== true) return false;
  maintenanceListener?.({
    message: body.message ?? null,
    until: body.until ?? null,
  });
  return true;
}

export async function cancellableFetchData<
  TMethodName extends keyof Omit<
    ReturnType<typeof getApiInstance>,
    "createCancellableRequest"
  >,
  TVariables,
  TResult = Awaited<ReturnType<ReturnType<typeof getApiInstance>[TMethodName]>>
>(
  api: ReturnType<typeof getApiInstance>,
  methodName: TMethodName,
  variables: TVariables,
  options?: {
    onSuccess?: (result: TResult) => void;
    onCancel?: () => void;
    onError?: (error: Error) => void;
    requestHeaders?: GraphQLClientRequestHeaders;
  }
): Promise<{
  promise: Promise<TResult>; // Removido undefined del tipo
  cancellableRequest: () => void;
}> {
  const cancellableRequest = api.createCancellableRequest(
    methodName,
    variables as any,
    options?.requestHeaders
  );

  const promise = (async () => {
    try {
      if (cancellableRequest.isCancelled()) {
        options?.onCancel?.();
        throw new Error("Request was cancelled");
      }

      const result = await cancellableRequest.promise;

      if (cancellableRequest.isCancelled()) {
        options?.onCancel?.();
        throw new Error("Request was cancelled");
      }

      if (options?.onSuccess) {
        options.onSuccess(result as TResult);
      }

      return result as TResult;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === "Request was cancelled") {
          options?.onCancel?.();
        } else {
          options?.onError?.(error);
        }
      }
      throw error;
    }
  })();

  return {
    promise,
    cancellableRequest: () => cancellableRequest.cancel(),
  };
}

export async function createApiFileLoader(
  url: string
): Promise<() => Promise<string>> {
  return async () => {
    const apiUrl =
      process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
    const serverUrl = apiUrl.replace(/\/graphql$/, "");
    const fullUrl = `${serverUrl}/download${url}`;

    const token = await tokenManager.getValidAccessToken();
    if (!token) throw new Error("No authentication token");

    const response = await fetch(fullUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  };
}

export function getApiInstance(accessToken: string, activityStore?: any) {
  const graphqlUrl = `${API_URL}/graphql`;
  const clientHeaders: HeadersInit = {
    authorization: accessToken !== null ? `Bearer ${accessToken}` : "",
  };

  const sdk = getSdk(
    new GraphQLClient(graphqlUrl, {
      headers: clientHeaders,
      credentials: "include",
    })
  );

  // Wrap SDK methods to handle token expiration and refresh + activity tracking + cancellation
  const wrappedSdk = new Proxy(sdk, {
    get(target, prop, receiver) {
      const originalMethod = Reflect.get(target, prop, receiver);

      // If it's not a function, return as is
      if (typeof originalMethod !== "function") {
        return originalMethod;
      }

      // Return wrapped function that handles 401 errors + activity tracking + cancellation
      return async (...args: any[]) => {
        const methodName = String(prop);
        const isMutation = /^(create|update|delete|save|remove)/i.test(
          methodName
        );
        const activityType = isMutation ? "mutation" : "query";

        // Extract entity ID if present in args
        let entityId: string | undefined;
        if (args[0] && typeof args[0] === "object") {
          entityId = args[0].id || args[0].entityId || args[0].where?.id;
        }

        // Build descriptive operation name
        const operationName = entityId
          ? `${methodName} #${entityId}`
          : methodName;

        // Start activity tracking and get abort signal
        const { activityId, signal } = activityStore.startActivity(
          activityType,
          operationName
        );

        // Create client with abort signal
        const client = new GraphQLClient(graphqlUrl, {
          headers: clientHeaders,
          signal,
          credentials: "include",
        });
        const cancelableSdk = getSdk(client);
        const cancelableMethod = Reflect.get(cancelableSdk, prop, receiver);

        try {
          const result = await cancelableMethod.apply(cancelableSdk, args);
          return result;
        } catch (error: any) {
          // Handle abort/cancellation
          if (error.name === "AbortError" || signal.aborted) {
            return; // Silent fail for cancelled requests
          }

          if (detectMaintenance(error)) {
            throw new Error("MAINTENANCE");
          }

          if (
            error?.response?.errors?.some(
              (e: any) =>
                e.extensions?.code === "UNAUTHENTICATED" ||
                e.message?.includes("Must be logged in")
            )
          ) {
            const currentToken = accessToken;
            const newToken = await tokenManager.getValidAccessToken();

            if (newToken && newToken !== currentToken) {
              const retryClient = new GraphQLClient(graphqlUrl, {
                headers: {
                  authorization: `Bearer ${newToken}`,
                },
                signal,
                credentials: "include",
              });
              const retrySdk = getSdk(retryClient);
              const retryMethod = Reflect.get(retrySdk, prop, receiver);
              return await retryMethod.apply(retrySdk, args);
            } else {
              tokenManager.clearTokens();
              throw new Error("SESSION_EXPIRED");
            }
          }
          throw error;
        } finally {
          // End activity tracking
          activityStore.endActivity(activityId);
        }
      };
    },
  });

  return {
    ...wrappedSdk,
    createCancellableRequest: <
      TMethodName extends keyof typeof sdk,
      TVariables,
      TResult
    >(
      methodName: TMethodName,
      variables: TVariables,
      requestHeaders?: GraphQLClientRequestHeaders
    ): CancellableRequest<TResult> => {
      let cancelled = false;
      const controller = new AbortController();

      const executeRequest = async (token?: string) => {
        const cancellableClient = new GraphQLClient(graphqlUrl, {
          headers: token
            ? {
                authorization: `Bearer ${token}`,
              }
            : clientHeaders,
          signal: controller.signal,
          credentials: "include",
        });

        const cancellableSdk = getSdk(cancellableClient);

        return (
          requestHeaders
            ? (cancellableSdk[methodName] as any)(variables, requestHeaders)
            : (cancellableSdk[methodName] as any)(variables)
        ) as Promise<TResult>;
      };

      const wrappedPromise = executeRequest().catch(async (error) => {
        if (error.name === "AbortError" || cancelled) {
          cancelled = true;
          throw new Error("Request was cancelled");
        }

        if (
          error?.response?.errors?.some(
            (e: any) =>
              e.extensions?.code === "UNAUTHENTICATED" ||
              e.message?.includes("Must be logged in")
          )
        ) {
          const newToken = await tokenManager.getValidAccessToken();
          if (newToken && !cancelled) {
            return executeRequest(newToken);
          }
        }

        throw error;
      });

      return {
        promise: wrappedPromise,
        cancel: () => {
          cancelled = true;
          controller.abort();
        },
        isCancelled: () => cancelled,
      };
    },
  };
}

/**
 * Generator function that obtains a valid access token (refreshing if needed)
 * and returns an API instance. This function MUST be used with `yield` in
 * MobX generator actions.
 *
 * Automatically updates the authStore with the refreshed token to keep the
 * store in sync with tokenManager.
 *
 * @example
 * ```typescript
 * *someMethod(): any {
 *   const api = yield* getApi(this.rootStore.authStore, this.rootStore.activityStore);
 *   const result = yield api.someQuery();
 * }
 * ```
 *
 * @param authStore - The AuthStore instance to update with the refreshed token
 * @param activityStore - ActivityStore for tracking operations (REQUIRED)
 * @throws {Error} If no valid token can be obtained (user must login again)
 * @returns API instance ready to use
 */
export function* getApi(
  authStore: AuthStoreInterface,
  activityStore: any
): Generator<
  Promise<string | null>,
  ReturnType<typeof getApiInstance>,
  string | null
> {
  const validToken = yield tokenManager.getValidAccessToken();

  if (!validToken) {
    throw new Error("SESSION_EXPIRED");
  }

  authStore.accessToken = validToken;

  return getApiInstance(validToken, activityStore);
}

/**
 * Async function that obtains a valid access token and returns an API instance.
 * Use this in async contexts (not MobX generators) where you need the API.
 *
 * @example
 * ```typescript
 * async function someAsyncMethod() {
 *   const api = await getApiAsync(authStore, activityStore);
 *   const result = await api.someQuery();
 * }
 * ```
 *
 * @param authStore - The AuthStore instance to update with the refreshed token
 * @param activityStore - ActivityStore for tracking operations (REQUIRED)
 * @throws {Error} If no valid token can be obtained (user must login again)
 * @returns API instance ready to use
 */
export async function getApiAsync(
  authStore: AuthStoreInterface,
  activityStore: any
): Promise<ReturnType<typeof getApiInstance>> {
  const validToken = await tokenManager.getValidAccessToken();

  if (!validToken) {
    throw new Error("SESSION_EXPIRED");
  }

  authStore.accessToken = validToken;

  return getApiInstance(validToken, activityStore);
}

export async function apiUploadFile(
  preflight = false,
  file: File,
  activityStore: any
) {
  const graphqlUrl = `${API_URL}/graphql`;
  // Cancelable a mano: mientras sube no cambió nada en el servidor, y el modal
  // de espera (UploadProgressDialog) ofrece cortarla. El detalle es lo que muestra.
  const { activityId, signal } = activityStore.startActivity(
    "mutation",
    "fileUpload",
    undefined,
    undefined,
    {
      manualCancellable: true,
      detail: file.name,
    }
  );

  const executeUpload = async (token: string) => {
    const clientHeaders: HeadersInit = {
      authorization: `Bearer ${token}`,
    };
    if (preflight) {
      clientHeaders["apollo-require-preflight"] = "true";
    }

    const formData = new FormData();
    const query = {
      query:
        "mutation fileUpload($file: Upload!) {\n  singleUpload(file: $file) {\n    ticket\n    filename\n    mimetype\n    encoding\n    size\n  }\n}\n",
      variables: { file: null },
      operationName: "fileUpload",
    };
    formData.append("operations", JSON.stringify(query));
    formData.append("map", `{ "1": ["variables.file"] }`);
    formData.append("1", file);

    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: clientHeaders,
      body: formData,
      signal,
      credentials: "include", // Send/receive cookies (2FA device trust)
    });

    const json = await response.json();

    if (response.status === 503 && json?.maintenance === true) {
      maintenanceListener?.({
        message: json.message ?? null,
        until: json.until ?? null,
      });
      throw new Error("MAINTENANCE");
    }

    if (!response.ok || !json.data) {
      // eslint-disable-next-line no-throw-literal -- domain-specific error shape consumed by 401 handler below
      throw {
        response: {
          status: response.status,
          errors: json.errors || [{ message: "Upload failed" }],
        },
      };
    }

    return json.data;
  };

  try {
    const validToken = await tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("No valid authentication token");
    }

    try {
      return await executeUpload(validToken);
    } catch (error: any) {
      if (signal.aborted) {
        return;
      }
      if (
        error?.response?.status === 401 ||
        error?.response?.errors?.some(
          (e: any) =>
            e.extensions?.code === "UNAUTHENTICATED" ||
            e.message?.includes("Must be logged in")
        )
      ) {
        const newToken = await tokenManager.getValidAccessToken();
        if (newToken && newToken !== validToken) {
          return await executeUpload(newToken);
        }
      }
      throw error;
    }
  } finally {
    activityStore.endActivity(activityId);
  }
}

export async function download(directory: string) {
  const downloadUrl = `${API_URL}/download`;

  const executeDownload = async (token: string) => {
    const response = await fetch(downloadUrl + directory, {
      method: "GET",
      headers: {
        authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (await detectMaintenanceResponse(response)) {
        throw new Error("MAINTENANCE");
      }
      // eslint-disable-next-line no-throw-literal -- domain-specific error shape consumed by 401 handler below
      throw {
        response: {
          status: response.status,
          errors: [{ message: `Download failed: ${response.statusText}` }],
        },
      };
    }

    return await response.blob();
  };

  const validToken = await tokenManager.getValidAccessToken();
  if (!validToken) {
    throw new Error("No valid authentication token");
  }

  try {
    return await executeDownload(validToken);
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await tokenManager.getValidAccessToken();
      if (newToken && newToken !== validToken) {
        return await executeDownload(newToken);
      }
    }
    throw error;
  }
}

export async function getHtmlContent(directory: string) {
  const executeGetHtml = async (token: string) => {
    const response = await fetch(API_URL + directory, {
      method: "GET",
      headers: {
        authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (await detectMaintenanceResponse(response)) {
        throw new Error("MAINTENANCE");
      }
      // eslint-disable-next-line no-throw-literal -- domain-specific error shape consumed by 401 handler below
      throw {
        response: {
          status: response.status,
          errors: [{ message: `Get HTML failed: ${response.statusText}` }],
        },
      };
    }

    const html = await response.text();
    const sanitizedHtml = DOMPurify.sanitize(html);
    return sanitizedHtml;
  };

  const validToken = await tokenManager.getValidAccessToken();
  if (!validToken) {
    throw new Error("No valid authentication token");
  }

  try {
    return await executeGetHtml(validToken);
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await tokenManager.getValidAccessToken();
      if (newToken && newToken !== validToken) {
        return await executeGetHtml(newToken);
      }
    }
    throw error;
  }
}

/**
 * REST fetch with automatic token refresh.
 * Use this for REST API calls instead of raw fetch.
 *
 * @example
 * const data = await restFetch('/api/dashboard/widgets');
 * const result = await restFetch('/api/endpoint', { method: 'POST', body: JSON.stringify(payload) });
 */
export async function restFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith("http") ? endpoint : `${API_URL}${endpoint}`;

  const executeRequest = async (token: string): Promise<Response> => {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const validToken = await tokenManager.getValidAccessToken();
  if (!validToken) {
    throw new Error("SESSION_EXPIRED");
  }

  let response = await executeRequest(validToken);

  // If 401, try to refresh token and retry
  if (response.status === 401) {
    const newToken = await tokenManager.getValidAccessToken();
    if (newToken && newToken !== validToken) {
      response = await executeRequest(newToken);
    } else {
      tokenManager.clearTokens();
      throw new Error("SESSION_EXPIRED");
    }
  }

  if (!response.ok) {
    if (await detectMaintenanceResponse(response)) {
      throw new Error("MAINTENANCE");
    }
    const errorData = await response.json().catch(() => ({}));
    // El mensaje es el código del server (o el estado HTTP); el error lleva además
    // el estado y el cuerpo, para quien necesite más (el retryAfter de un 429).
    throw Object.assign(
      new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`
      ),
      {
        status: response.status,
        body: errorData,
      }
    );
  }

  return response.json();
}
